/** Copyright © 2013-2021 DataYes, All Rights Reserved. */

import { useCallback, useMemo, Dispatch, SetStateAction } from 'react';
import { parseUrl, stringifyUrl } from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

type Query = Record<string, string | undefined>;
type SetableQuery = Record<string, number | string | undefined>;
type PatchFn = Dispatch<SetStateAction<SetableQuery>>;

export function useQuery() {
  const { search } = useLocation();
  const query = useMemo(() => parseUrl(search).query, [search]);

  return query as Query;
}

export function useQueryState(): [Query, PatchFn] {
  const query = useQuery();
  const { replace } = useHistory();

  const setQuery = useCallback<PatchFn>(
    (patch) => {
      const { pathname, search } = window.location;
      const prevQuery = parseUrl(search).query as SetableQuery;
      const url = stringifyUrl({
        url: pathname,
        query: typeof patch === 'function' ? patch(prevQuery) : patch,
      });
      replace(url);
    },
    [replace]
  );

  return [query, setQuery];
}
